import meleElement from 'meleElement';
import { VideoDataStream, CameraHandler, CameraSwitcher, CameraTorchController, STATE } from '@soitron/camera-handler';
import { parseRatio, shallowAssign, timeDelay } from '@soitron/global-helpers';
import { SliderController } from './src/region-of-interest/slider/slider-controller';
import { RegionOfInterest } from './src/region-of-interest/region-of-interest';
import { ControlsController } from './src/controls/controls';
import layout from './src/layout';

/**
 *
 * @param {HTMLElement} el
 */
async function toggleFullScreen(el) {
  if (!document.fullscreenElement) {
    await el.requestFullscreen();
  } else {
    document.exitFullscreen?.();
  }
}

/**
 * @typedef {Object} ROIData
 * Configuration data for the Region of Interest area.
 * @property {number} [x] - The x-coordinate offset.
 * @property {number} [y] - The y-coordinate offset.
 * @property {number} [width] - The width of the ROI.
 * @property {number} [height] - The height of the ROI.
 * @property {number} [scale] - The current zoom/scale factor.
 */

/**
 * @typedef {('environment'|'user')} FacingMode
 * The facing mode of the camera.
 */

/**
 * Configuration for component properties.
 * These properties are automatically bound to HTML attributes.
 */
const PROPERTIES = shallowAssign(
  {},
  {
    debug: {
      type: 'boolean',
      default: false,
      description: '',
    },

    // --- Camera Configuration ---
    camera: {
      type: 'string',
      default: 'environment',
      description: 'Camera device ID or facing mode ("environment" for back, "user" for front).',
    },
    videowidth: {
      type: 'number',
      default: 1920,
      description: 'Desired video resolution width in pixels.',
    },
    videoheight: {
      type: 'number',
      default: 1080,
      description: 'Desired video resolution height in pixels.',
    },
    videoloadtime: {
      type: 'number',
      default: 10000,
      description: 'Timeout in milliseconds for loading the video stream metadata before throwing an error.',
    },
    // Video Image Capture stream
    datastream: {
      type: 'boolean',
      default: true,
      description: 'If true, enables real-time video data stream frame capture and mmFrame messages.',
    },
    processingFrequency: {
      type: 'number',
      default: 15,
      description: 'Frequency (FPS) at which the data stream processes frames.',
    },
    outputSize: {
      type: 'number',
      default: 640,
      description: 'Target size (width) of the ROI image data in pixels.',
    },
    // --- Feature Flags ---
    fadein: { type: 'boolean', default: true, description: 'If true, applies a CSS fade-in effect when the camera becomes ready.' },
    autoplay: { type: 'boolean', default: true, description: 'If true, the camera stream starts automatically when the component initializes.' },
    // --- Controls Flags ---
    controls: { type: 'boolean', default: true, description: 'If true, renders control panel.' },
    enableSwitchControls: {
      type: 'boolean',
      default: false,
      description: 'If true, enables the logic and UI for switching between front/back cameras.',
    },
    enableTorchControls: { type: 'boolean', default: false, description: 'If true, enables the logic and UI for controlling the camera flashlight.' },

    // --- ROI (Region of Interest) Configuration ---
    roiDraw: { type: 'boolean', default: true, description: 'If true, renders the Region of Interest (ROI) rectangle overlay on video frames.' },

    roiScaleFactorValue: { type: 'number', default: 1.5, description: 'Initial zoom scale factor.' },
    roiScaleFactorMin: { type: 'number', default: 0.5, description: 'Minimum zoom scale factor.' },
    roiScaleFactorMax: { type: 'number', default: 2.5, description: 'Maximum zoom scale factor.' },
    roiScaleFactorStep: { type: 'number', default: 0.025, description: 'Step size for zoom adjustment.' },

    roiRatio: { type: 'string', default: '1/1', description: 'Aspect ratio of the ROI rectangle (e.g., "16/9", "16:9", "4-3").' },
    roiWidth: { type: 'number', default: 300, description: 'Initial width of the ROI rectangle in pixels.' },
    roiHorizontalOffset: { type: 'number', default: 0, description: '.' },
    roiVerticalOffset: { type: 'number', default: 0, description: '' },
    roiCenter: { type: 'boolean', default: true, description: '' },
    roiX: { type: 'number', default: 0, description: '' },
    roiY: { type: 'number', default: 0, description: '' },
    roiBoxSelector: { type: 'string', default: '.box', description: '' },
    roiPadding: { type: 'number', default: 0, description: '' },

    // --- Event Callbacks ---
    eventStart: { type: 'function', description: 'Callback invoked when camera successfully starts.' },
    eventPlay: { type: 'function', description: 'Callback invoked when video playback begins.' },
    eventStop: { type: 'function', description: 'Callback invoked when camera stream is stopped.' },
    eventPause: { type: 'function', description: 'Callback invoked when video is paused.' },
    eventRestart: { type: 'function', description: 'Callback invoked when video is restarted.' },
    eventCameraToggle: { type: 'function', description: 'Callback invoked when camera device is switched.' },
    eventCameraLight: { type: 'function', description: 'Callback invoked when camera torch state changes.' },
    eventChangeControlsPosition: { type: 'function', description: '' },
  },
  meleElement._properties
);

/**
 * A Web Component that manages camera video streaming, Region of Interest (ROI) selection,
 * and real-time frame data processing.
 *
 * @element mele-camera
 * @extends {meleElement}
 *
 * @example
 * // HTML Usage
 * <mele-camera
 *   camera="environment"
 *   videowidth="1920"
 *   autoplay="true"
 *   roi-draw="true"
 * ></mele-camera>
 *
 * @example
 * // JS Usage
 * const cam = document.querySelector('mele-camera');
 * await cam.cameraInit();
 * cam.onDataStream((frameData) => {
 *   console.log('Frame processed:', frameData);
 * });
 */
export default class MeleCamera extends meleElement {
  /**
   * Static property definitions for meleElement to bind attributes.
   * @type {Object}
   */
  static _properties = PROPERTIES;

  /**
   * List of attributes to observe for changes.
   * @type {string[]}
   */
  static observedAttributes = Object.keys(PROPERTIES);

  _videoElement = null;
  /** @type {CameraHandler|null} */
  _cameraHandler = null;
  /** @type {VideoDataStream|null} */
  _cameraDataStream = null;
  /** @type {CameraSwitcher|null} */
  _cameraSwitcher = null;
  /** @type {CameraTorchController|null} */
  _cameraTorch = null;
  /** @type {ControlsController|null} */
  _controls = null;
  /** @type {RegionOfInterest|null} */
  _roi = null;
  /** @type {SliderController|null} */
  _slider = null;

  _cameraOnCancel = null;
  _cameraOnChangePosition = null;

  // ===========================================================================
  // LIFECYCLE
  // ===========================================================================

  /**
   * Lifecycle hook called when the element is initialized.
   * Sets up the DOM layout and references.
   * @override
   * @returns {Promise<void>}
   */
  async init() {
    this.appendChildren(layout);
    this.classList.add('camera-video-element');
    this._videoElement = this.find('video');
    super.init();
  }

  /**
   * Main initialization method to start the camera logic.
   * Can be called manually if the component is not set to autoplay,
   * or if specific initialization timing is required.
   *
   * @public
   * @returns {Promise<MeleCamera>} Returns the component instance for chaining.
   */
  async cameraInit() {
    // 1. UI Setup
    if (!this.fadein) this.classList.add('show');
    if (this.controls) this._setupControls();

    // 2. Core Handler Setup
    this._setupCameraHandler();

    // await timeDelay(5000);
    setTimeout(() => {
      // 3. Feature Setup
      // if (this.enableSwitchControls)
      this._setupSwitcher();
      // if (this.enableTorchControls)
      this._setupTorch();
      // if (this.datastream)
      this._setupDataStream();
    }, 2000);

    //this.find('.fullscreen').addEventListener('click', () => toggleFullScreen(this));

    return this;
  }

  // ===========================================================================
  // PUBLIC API (Playback & Control)
  // ===========================================================================

  /**
   * Starts the camera stream.
   * Triggers the `eventStart` callback.
   *
   * @public
   * @param {boolean} [autoplay] - If true, immediately calls `play()` after stream start.
   * @returns {Promise<void>}
   */
  async start(autoplay) {
    this.eventStart?.(autoplay);
    await this._cameraHandler?.__startStream();
    if (autoplay) {
      await this.play();
    }
  }

  /**
   * Plays the video playback and starts data streaming.
   * Applies the current ROI configuration and triggers `eventPlay`.
   *
   * @public
   * @returns {Promise<void>}
   */
  async play() {
    await this._cameraHandler?.__playVideoPlayback();
    this._cameraDataStream?.__start();
    this._roi?.__applyConfiguration?.();

    if (this.fadein) this.classList.add('show');
    this.eventPlay?.();
  }

  /**
   * Stops the camera stream and data processing.
   * Removes the visual class and triggers `eventStop`.
   *
   * @public
   * @returns {Promise<void>}
   */
  async stop() {
    if (this.fadein) this.classList.remove('show');

    this._cameraDataStream?.__stop();
    await this._cameraHandler?.__stopStream();
    this.eventStop?.();
  }

  /**
   * Pauses the video playback.
   * Triggers `eventPause`.
   *
   * @public
   * @returns {Promise<void>}
   */
  async pause() {
    await this._cameraHandler?.__pauseVideoPlayback();
    this.eventPause?.();
  }

  /**
   * Restarts the camera stream and playback.
   * Useful when stream is interrupted or device configuration changes.
   * Triggers `eventRestart`.
   *
   * @public
   * @returns {Promise<void>}
   */
  async restart() {
    await this._cameraHandler?.__restartStreamAndPlay();
    this.eventRestart?.();
  }

  // ===========================================================================
  // FEATURES (Switch & Light)
  // ===========================================================================

  /**
   * Toggles between available camera devices (e.g., front/back).
   * Does nothing if switching is disabled or unavailable.
   * Triggers `eventCameraToggle` with the new facing mode.
   *
   * @public
   * @returns {Promise<void>}
   */
  async cameraToggle() {
    if (!this._cameraSwitcher || !this.canToggle) return;

    await this._cameraSwitcher.__toggleCameraDevice();
    this.eventCameraToggle?.(this._cameraSwitcher.__getCurrentCameraFacingMode);
  }

  /**
   * Toggles the camera flashlight (torch).
   * Does nothing if the device does not support a torch.
   * Triggers `eventCameraLight` with the new state boolean.
   *
   * @public
   * @param {boolean} [turnOnState] - Force a specific state (true=on, false=off). If undefined, toggles.
   * @returns {Promise<void>}
   */
  async cameraLight(turnOnState) {
    if (!this._cameraTorch || !this.hasLight) return;

    await this._cameraTorch.__toggleTorch(turnOnState);
    this.eventCameraLight?.(this._cameraTorch.__getIsCurrentlyOn);
  }

  // ===========================================================================
  // GETTERS & STATE
  // ===========================================================================

  /**
   * Returns the frozen object of possible camera states.
   * @readonly
   * @returns {Object} Enum of camera states.
   */
  get stateList() {
    return Object.freeze(STATE);
  }

  /**
   * Returns the current internal state of the camera handler.
   * @readonly
   * @returns {string|number} The current state.
   */
  get state() {
    return this._cameraHandler?.__getCurrentState;
  }

  /**
   * Checks if camera switching is available on this device.
   * @readonly
   * @returns {boolean}
   */
  get canToggle() {
    return !!this._cameraHandler?.__canToggleCamera;
  }

  /**
   * Checks if the camera torch (flashlight) is supported and available.
   * @readonly
   * @returns {boolean}
   */
  get hasLight() {
    return !!this._cameraTorch?.__checkIsSupported;
  }

  // ===========================================================================
  // EVENT BINDINGS
  // ===========================================================================

  /**
   * Registers a callback for the UI cancel action.
   * @public
   * @param {Function} callback - Function to execute when cancel is clicked.
   */
  onCancel(callback) {
    if (!callback) return;
    this._cameraOnCancel = callback;
    this._controls?.__actionCancel(() => this._cameraOnCancel?.());
  }

  /**
   * Registers a callback for the ROI position change action.
   * Also binds the UI logic to toggle slider position.
   * @public
   * @param {Function} callback - Function to execute when position button is clicked.
   */
  onChangePosition(callback) {
    if (!callback) return;
    this._cameraOnChangePosition = callback;

    this._controls?.__actionPosition(() => {
      this._slider?.__togglePositionLeft();
      this._controls?.__positionToggle();

      this._roi?.__applyConfiguration({
        horizontalOffset: this._roi?.__configuration?.horizontalOffset !== 60 ? 60 : -60,
      });

      this._cameraOnChangePosition?.();
    });
  }

  /**
   * Registers a callback invoked when the camera handler is fully ready.
   * @public
   * @param {Function} callback
   */
  onReady(callback) {
    this._cameraHandler?.__onReady(callback);
  }

  /**
   * Registers a listener for the video data stream.
   * This function receives the processed frame data.
   * @public
   * @param {TDataStreamCallback} callback
   */
  onDataStream(callback) {
    /**
     * @type {HTMLCanvasElement}
     */
    let canvas;
    /**
     * @type {CanvasRenderingContext2D}
     */
    let ctx;
    if (this.debug) {
      canvas = this.find('.box > canvas');
      if (!canvas) {
        canvas = document.createElement('canvas');
        // canvas.style.width = canvas.style.height = '100%';
        // if (this._cameraSwitcher?.__getCurrentCameraFacingMode !== 'environment') canvas.style.transform = 'scaleX(-1)';
        this.find('.box').prepend(canvas);
      }
      ctx = canvas.getContext('2d');
    }

    this._cameraDataStream?.__onDataStream((id, streamData, metadata) => {
      callback?.(id, streamData, metadata);

      if (this.debug) {
        canvas.width = streamData.width;
        canvas.height = streamData.height;
        ctx.putImageData(streamData, 0, 0);
      }
    });
  }

  /**
   * Removes the data stream listener.
   * @public
   */
  removeOnDataStream() {
    this._cameraDataStream?.__removeOnDataStream();
  }

  // ===========================================================================
  // PRIVATE HELPERS (Initialization Logic)
  // ===========================================================================

  /**
   * Initializes the overlay controls (buttons).
   * @private
   */
  _setupControls() {
    this._controls = new ControlsController(this.find('.camera-controls-container'));
    this._controls.__show();
  }

  /**
   * Initializes the core CameraHandler logic.
   * Creates a video element if one does not exist in the layout.
   * @private
   */
  _setupCameraHandler() {
    this._cameraHandler = new CameraHandler(this._videoElement, {
      __cameraVideoConfig: {
        facingMode: { exact: this.camera },
        width: { ideal: this.videowidth },
        height: { ideal: this.videoheight },
      },
      __global: { __videoLoadTime: this.videoloadtime },
    });

    // Fallback if video element wasn't found in DOM
    if (!this._videoElement) {
      const generatedVideo = this._cameraHandler.__getVideoElement;
      generatedVideo.classList.add('camera-video', 'camera-video-autogenerated');
      this.prepend(generatedVideo);
    }
  }

  /**
   * Initializes the camera switcher logic if enabled.
   * @private
   */
  _setupSwitcher() {
    this._cameraSwitcher = new CameraSwitcher(this._cameraHandler);
    if (this.canToggle) {
      this._controls?.__actionSwitch(() => this.cameraToggle());
    }
  }

  /**
   * Initializes the torch controller logic if enabled.
   * @private
   */
  _setupTorch() {
    this._cameraTorch = new CameraTorchController(this._cameraHandler);
    if (this.hasLight) {
      this._controls?.__actionLight(() => this.cameraLight());
    }
  }

  /**
   * Initializes the VideoDataStream and ROI logic.
   * @private
   */
  _setupDataStream() {
    this._cameraDataStream = new VideoDataStream(this._cameraHandler, {
      minOutputSize: this.outputSize,
      processingFrequency: this.processingFrequency,
    });

    if (this.roiDraw) {
      this._setupROI();
    }
  }

  /**
   * Initializes the Region of Interest (ROI) visual elements.
   * @private
   */
  _setupROI() {
    const roiContainer = this.find('.region-of-interest-container');
    if (!roiContainer) return;

    this._roi = new RegionOfInterest(roiContainer, {
      width: this.roiWidth,
      ratio: parseRatio(this.roiRatio),
      scale: this.roiScaleFactorValue,
      horizontalOffset: this.roiHorizontalOffset,
      verticalOffset: this.roiVerticalOffset,
      center: this.roiCenter,
      x: this.roiX,
      y: this.roiY,
      boxSelector: this.roiBoxSelector,
      padding: this.roiPadding,
    });

    // Sync ROI resize events with data stream config
    this._roi.__registerResizeHandler(data => this._cameraDataStream?.__updateConfig(data));

    if (this.controls) this._setupSlider();
  }

  /**
   * Initializes the Zoom slider control.
   * @private
   */
  _setupSlider() {
    this._slider = new SliderController(this.find('.slider-wrapper'));

    this._slider.__onValueChange(value => {
      this._roi?.__applyConfiguration({ scale: value });
    });

    this._slider.__min = this.roiScaleFactorMin;
    this._slider.__max = this.roiScaleFactorMax;
    this._slider.__step = this.roiScaleFactorStep;
    this._slider.__value = this.roiScaleFactorValue;

    this._controls?.__actionZoom(() => this._slider?.__toggleVisibility());
  }
}

window.customElements.define('mele-camera', MeleCamera);
